/**
 * Coco Daddy Poster — Save for later + restore
 * Theme asset: coco-daddy-save-later.js
 *
 * Load AFTER coco-daddy-poster.js
 *
 * Return URL example:
 *   /products/daddy-me-football-poster?mode=order&save_id=2485210
 *
 * POST /save-for-dad-poster
 * GET  /save-for-dad-poster/{save_id}
 *
 * Upload flow: photo → Shopify Files (cart/add.js file property) → CDN URL in fields.upload
 * Save flow: POST /save-for-dad-poster (JSON only, no separate /upload route)
 *
 * POST body (save):
 * {
 *   email, consent, name, product_url, product_id,
 *   fields: { upload: "https://cdn.shopify.com/.../photo.jpg", rugby_team, names, … },
 *   framing: { product, size, frame, people, rush }
 * }
 * Note: `name` = save-modal customer name (#saveName).
 *       `fields.names` = poster dedication (e.g. "Love, the kids").
 *       `fields.upload` = https URL only (upload via Shopify first; never base64).
 *
 * Success 200:
 * { ok: true, status: "success", message, save_id, return_url, … }
 *
 * Failed 4xx/5xx:
 * { ok: false, status: "failed", error: { code, message } }
 *
 * GET /save-for-dad-poster/{save_id}
 * Success: { ok, status, save_id, product_id, fields, framing, … }
 * Failed:  400 INVALID_SAVE | 404 SAVE_NOT_FOUND | 410 SAVE_EXPIRED
 * On ?mode=order&save_id=… → GET, if ok preselect + open Review.
 * ----------------------------------------------------------
 */
(function () {
  'use strict';

  const cfg = window.CocoDaddyPoster || {};
  const api = () => window.CocoDaddyPosterAPI || null;

  const PRODUCT_PARAMS = {
    9541122261276: {
      sport: 'NRL Rugby',
      fieldsList: 'rugby_team:req,stadium:req,team_song:opt,names:req,est:req,message:opt,upload:req',
      teamKey: 'rugby_team',
    },
    9526848913692: {
      sport: 'AFL Footy',
      fieldsList: 'afl_team:req,stadium:req,team_song:opt,names:req,est:req,message:opt,upload:req',
      teamKey: 'afl_team',
    },
    10837369356572: {
      sport: 'Soccer',
      fieldsList: 'football_team:req,names:req,message:opt,upload:req',
      teamKey: 'football_team',
    },
    8691763347740: {
      sport: 'Dad',
      fieldsList: 'bg_colour:req,style:req,dad_name:req,est:opt,message:opt,names:req,upload:req',
      teamKey: '',
    },
  };

  const FRAME_OPTIONS = {
    product: ['digital', 'print', 'frame'],
    size: ['30x40cm', '45x60cm'],
    frame: [
      { key: 'natural', label: 'Oak' },
      { key: 'black', label: 'Black' },
      { key: 'white', label: 'White' },
      { key: 'none', label: 'No frame' },
    ],
  };

  const STORAGE_PREFIX = 'coco_daddy_save_';
  const API_BASE = (
    cfg.saveLaterApiBase ||
    'https://coco-studio-worker-production.up.railway.app'
  ).replace(/\/$/, '');
  // POST /save-for-dad-poster
  // GET  /save-for-dad-poster/{save_id}
  const SAVE_ENDPOINT =
    cfg.saveLaterEndpoint || `${API_BASE}/save-for-dad-poster`;
  const GET_BASE =
    cfg.saveLaterGetEndpoint || `${API_BASE}/save-for-dad-poster`;

  function isHttpUrl(value) {
    return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
  }

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function getProductMeta() {
    const id = String(cfg.productId || '');
    if (PRODUCT_PARAMS[id]) return { id, ...PRODUCT_PARAMS[id] };
    const sport = cfg.sport || '';
    const found = Object.entries(PRODUCT_PARAMS).find(([, meta]) => meta.sport === sport);
    if (found) return { id: found[0], ...found[1] };
    return {
      id,
      sport: sport || 'Unknown',
      fieldsList: cfg.fieldsList || '',
      teamKey: cfg.teamKey || 'team',
    };
  }

  function collectFields(posterState, uploadUrl) {
    const meta = getProductMeta();
    const teamKey = meta.teamKey || cfg.teamKey || 'team';
    const st = posterState || api()?.getState?.() || {};
    const fields = {};

    const teamVal = qs('#teamInput')?.value || '';
    if (teamVal) fields[teamKey] = teamVal;

    const stadium = qs('#backgroundInput')?.value || '';
    if (stadium) fields.stadium = stadium;

    const bgColour = qs('#bgColourInput')?.value || '';
    if (bgColour) fields.bg_colour = bgColour;

    const styleVal = qs('#styleInput')?.value || '';
    if (styleVal) fields.style = styleVal;

    const dadName = qs('#dadNameInput')?.value || '';
    if (dadName) fields.dad_name = dadName;

    if (qs('#teamSong')) {
      fields.team_song = qs('#teamSong').checked ? 'Yes' : 'No';
    }

    const names = qs('#posterTitleInput')?.value || '';
    if (names) fields.names = names;

    const est = qs('#estYear')?.value || '';
    if (est) fields.est = est;

    const message = qs('#messageInput')?.value || '';
    if (message) fields.message = message;

    const resolvedUpload =
      (uploadUrl && isHttpUrl(uploadUrl) ? uploadUrl : '') ||
      (st.photoCdnUrl && isHttpUrl(st.photoCdnUrl) ? st.photoCdnUrl : '') ||
      (st.photoData && isHttpUrl(st.photoData) ? st.photoData : '');
    if (resolvedUpload) fields.upload = resolvedUpload;

    return fields;
  }

  /**
   * Ensure fields.upload is a remote https URL — upload via Shopify cart flow when needed.
   * @returns {Promise<string>} CDN URL or '' when no photo
   */
  async function resolveUploadUrl(posterState) {
    const st = posterState || api()?.getState?.() || {};

    if (st.photoCdnUrl && isHttpUrl(st.photoCdnUrl)) return st.photoCdnUrl;
    if (st.photoData && isHttpUrl(st.photoData)) return st.photoData;

    const uploadFn = api()?.uploadPhotoForSave;
    if (typeof uploadFn !== 'function') {
      throw new Error('Photo upload is not available. Please refresh and try again.');
    }

    return uploadFn();
  }

  function collectFraming(posterState) {
    const st = posterState || api()?.getState?.() || {};
    const peopleNum = Number(st.people);
    return {
      product: st.product || '',
      size: st.size || '',
      frame: st.frame || '',
      people: Number.isFinite(peopleNum) && peopleNum > 0 ? peopleNum : 2,
      rush: !!st.rush,
    };
  }

  /** Exact POST body shape for the save-later API */
  function buildPayload({ email, consent, posterState, name, yourName, uploadUrl }) {
    const meta = getProductMeta();
    const customerName = String(name || yourName || '').trim();
    const payload = {
      email: String(email || '').trim(),
      consent: consent !== false,
      product_url: cfg.productUrl || window.location.pathname,
      product_id: String(meta.id || cfg.productId || ''),
      fields: collectFields(posterState, uploadUrl),
      framing: collectFraming(posterState),
    };
    if (customerName) {
      payload.name = customerName;
    }
    if (meta.sport || cfg.sport) payload.sport = meta.sport || cfg.sport;
    return payload;
  }

  function storeLocal(saveId, payload) {
    try {
      localStorage.setItem(
        STORAGE_PREFIX + saveId,
        JSON.stringify({
          ...payload,
          save_id: saveId,
          fields: payload.fields,
          framing: payload.framing,
        })
      );
    } catch (e) {
      console.warn('[coco-daddy-save] localStorage write failed', e);
    }
  }

  function readLocal(saveId) {
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + saveId);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function buildReturnUrl(saveId) {
    const base = cfg.productUrl || window.location.pathname;
    const url = new URL(base, window.location.origin);
    url.searchParams.set('mode', 'order');
    url.searchParams.set('save_id', String(saveId));
    return url.toString();
  }

  function parseErrorMessage(data, fallback) {
    if (!data) return fallback;
    if (data.error && typeof data.error === 'object') {
      return data.error.message || data.error.code || fallback;
    }
    if (typeof data.error === 'string') return data.error;
    if (data.message && data.ok === false) return data.message;
    return fallback;
  }

  /**
   * Save design — checks HTTP status and body.ok
   * @returns {Promise<{ok:boolean, status:string, message?:string, save_id?:string, return_url?:string, error?:object}>}
   */
  async function saveDesign({ name, email, consent, posterState, uploadUrl }) {
    let resolvedUpload = uploadUrl || '';
    if (!resolvedUpload || !isHttpUrl(resolvedUpload)) {
      resolvedUpload = await resolveUploadUrl(posterState);
    }

    const payload = buildPayload({
      email,
      consent,
      posterState,
      name,
      yourName: name,
      uploadUrl: resolvedUpload,
    });

    let res;
    let data = null;
    try {
      res = await fetch(SAVE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      data = await res.json().catch(() => null);
    } catch (networkErr) {
      return {
        ok: false,
        status: 'failed',
        error: {
          code: 'NETWORK_ERROR',
          message: networkErr.message || 'Network error. Please try again.',
        },
      };
    }

    // Prefer body.ok; also treat non-2xx as failed
    const bodyOk = data && data.ok === true && (data.status === 'success' || !data.status);
    const httpOk = res.ok;

    if (httpOk && (bodyOk || (data && data.save_id && data.ok !== false))) {
      const saveId = String(data.save_id || '');
      const returnUrl = data.return_url || data.link || data.url || (saveId ? buildReturnUrl(saveId) : '');
      if (saveId) storeLocal(saveId, payload);
      return {
        ok: true,
        status: 'success',
        message: data.message || 'Design saved. Check your email for the return link.',
        save_id: saveId,
        return_url: returnUrl,
        raw: data,
      };
    }

    return {
      ok: false,
      status: 'failed',
      error:
        data && data.error
          ? data.error
          : {
              code: data?.error?.code || 'SAVE_FAILED',
              message: parseErrorMessage(data, `Could not save design (${res.status}).`),
            },
      raw: data,
    };
  }

  /**
   * GET /save-for-dad-poster/{save_id}
   * Success: { ok:true, status:"success", fields, framing, … }
   * Failed:  { ok:false, status:"failed", error:{ code, message } }
   * HTTP: 400 INVALID_SAVE | 404 SAVE_NOT_FOUND | 410 SAVE_EXPIRED
   */
  async function fetchSavedDesign(saveId) {
    const id = String(saveId || '').trim();
    if (!id) {
      return {
        ok: false,
        status: 'failed',
        http: 400,
        error: { code: 'INVALID_SAVE', message: 'Missing save_id.' },
      };
    }

    const base = String(GET_BASE || '').replace(/\/$/, '');
    const endpoint = `${base}/${encodeURIComponent(id)}`;

    let res;
    let data = null;
    try {
      res = await fetch(endpoint, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      data = await res.json().catch(() => null);
    } catch (networkErr) {
      const local = readLocal(id);
      if (local && (local.fields || local.framing)) {
        return { ok: true, status: 'success', save_id: id, ...local, remote: false };
      }
      return {
        ok: false,
        status: 'failed',
        http: 0,
        error: {
          code: 'NETWORK_ERROR',
          message: networkErr.message || 'Network error loading saved design.',
        },
      };
    }

    if (res.ok && data && data.ok === true && (data.status === 'success' || data.fields || data.framing)) {
      return {
        ok: true,
        status: 'success',
        save_id: String(data.save_id || id),
        product_id: data.product_id,
        product: data.product,
        product_label: data.product_label,
        expires_at: data.expires_at,
        fields: data.fields || {},
        framing: data.framing || {},
        remote: true,
        raw: data,
      };
    }

    const codeFromHttp =
      res.status === 400
        ? 'INVALID_SAVE'
        : res.status === 404
          ? 'SAVE_NOT_FOUND'
          : res.status === 410
            ? 'SAVE_EXPIRED'
            : 'SAVE_FAILED';

    const error =
      data && data.error
        ? data.error
        : {
            code: codeFromHttp,
            message: parseErrorMessage(
              data,
              res.status === 410
                ? 'This saved design link has expired.'
                : res.status === 404
                  ? `Saved design not found (${id}).`
                  : `Could not load saved design (${res.status}).`
            ),
          };

    // Local fallback only when remote truly missing and we have a cache
    if (res.status === 404 || res.status === 410) {
      return { ok: false, status: 'failed', http: res.status, error, raw: data };
    }

    const local = readLocal(id);
    if (local && (local.fields || local.framing)) {
      return { ok: true, status: 'success', save_id: id, ...local, remote: false };
    }

    return { ok: false, status: 'failed', http: res.status, error, raw: data };
  }

  function normalizeDesignForApply(result) {
    if (!result || !result.ok) return null;
    const fields = { ...(result.fields || {}) };
    const framing = { ...(result.framing || {}) };
    let photo = result.photo || null;

    const upload = fields.upload;
    if (upload && typeof upload === 'string' && !photo) {
      if (/^https?:\/\//i.test(upload)) {
        photo = { url: upload, name: 'saved-photo.jpg' };
      } else if (upload.startsWith('data:')) {
        photo = { dataUrl: upload, name: 'saved-photo.jpg' };
      }
    }
    // Prefer CDN url when both exist
    if (photo?.url && photo?.dataUrl) {
      photo = { url: photo.url, name: photo.name || 'saved-photo.jpg' };
    }

    return {
      save_id: result.save_id,
      product_id: result.product_id,
      fields,
      framing,
      photo,
    };
  }

  function getQueryParams() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (e) {
      return new URLSearchParams();
    }
  }

  async function restoreFromUrl() {
    const params = getQueryParams();
    const mode = (params.get('mode') || '').toLowerCase();
    const saveId = params.get('save_id');
    if (mode !== 'order' || !saveId) return false;

    const poster = api();
    if (!poster || typeof poster.applySavedDesign !== 'function') {
      console.warn('[coco-daddy-save] CocoDaddyPosterAPI.applySavedDesign missing');
      return false;
    }

    const result = await fetchSavedDesign(saveId);
    if (!result.ok) {
      const msg =
        result.error?.message ||
        (result.error?.code === 'SAVE_EXPIRED'
          ? 'This saved design link has expired.'
          : 'Saved design not found.');
      poster.showToast?.(msg);
      return false;
    }

    const design = normalizeDesignForApply(result);
    if (!design || (!design.fields && !design.framing)) {
      poster.showToast?.('Saved design not found.');
      return false;
    }

    const ok = poster.applySavedDesign(design, {
      goToReview: true,
      openWizard: true,
      previewMode: true,
    });
    if (ok) poster.showToast?.('Your saved design has been restored.');
    return ok;
  }

  function showFormError(msg) {
    const el = qs('#saveFormError');
    if (!el) {
      api()?.showToast?.(msg);
      return;
    }
    el.hidden = !msg;
    el.textContent = msg || '';
  }

  function bindSaveForm() {
    const form = qs('#saveForm');
    const modal = qs('#saveModal');
    if (!form || form.dataset.cocoSaveBound === '1') return;
    form.dataset.cocoSaveBound = '1';

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      showFormError('');

      const name = qs('#saveName')?.value.trim() || '';
      const email = qs('#saveEmail')?.value.trim() || '';
      const consentEl = qs('#saveConsent');
      const consent = consentEl ? !!consentEl.checked : true;

      if (!name) {
        showFormError('Please enter your name.');
        qs('#saveName')?.focus();
        return;
      }
      if (!email) {
        showFormError('Please enter your email address.');
        qs('#saveEmail')?.focus();
        return;
      }
      if (consentEl && !consent) {
        showFormError('Please agree so we can email your return link.');
        consentEl.focus();
        return;
      }

      const submitBtn = form.querySelector('button[type="submit"]');
      const prevLabel = submitBtn?.textContent || 'Send me the link';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Saving…';
      }

      try {
        const posterState = api()?.getState?.() || null;
        let uploadUrl = '';
        if (submitBtn) submitBtn.textContent = 'Uploading photo…';
        try {
          uploadUrl = await resolveUploadUrl(posterState);
        } catch (uploadErr) {
          const msg = uploadErr.message || 'Could not upload photo. Please try again.';
          showFormError(msg);
          api()?.showToast?.(msg);
          return;
        }
        if (submitBtn) submitBtn.textContent = 'Saving…';
        const result = await saveDesign({ name, email, consent, posterState, uploadUrl });

        if (!result.ok) {
          const msg =
            result.error?.message ||
            result.message ||
            'Could not save design. Please try again.';
          showFormError(msg);
          api()?.showToast?.(msg);
          return;
        }

        const success = qs('#saveSuccessText');
        if (success) {
          success.textContent =
            result.message ||
            `Thanks${name ? ` ${name}` : ''}! We've sent your design link to ${email}.`;
        }
        modal?.classList.add('success');

        document.dispatchEvent(
          new CustomEvent('coco:save-design-complete', {
            detail: { name, email, consent, ...result },
          })
        );
      } catch (err) {
        const msg = err.message || 'Could not save design. Please try again.';
        showFormError(msg);
        api()?.showToast?.(msg);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = prevLabel;
        }
      }
    });
  }

  function init() {
    window.CocoDaddySaveLater = {
      PRODUCT_PARAMS,
      FRAME_OPTIONS,
      buildPayload,
      resolveUploadUrl,
      saveDesign,
      fetchSavedDesign,
      restoreFromUrl,
      buildReturnUrl,
      normalizeDesignForApply,
    };

    bindSaveForm();

    const tryRestore = () => {
      if (!api()?.applySavedDesign) {
        setTimeout(tryRestore, 50);
        return;
      }
      restoreFromUrl();
    };
    tryRestore();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
